/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
// eslint-disable-line no-console
import { DSControlledDateTimePicker } from '../index.js';
import type { DSControlledDateTimePickerT } from '../index.js';

// test we expose the namespace and the namespace follows our deliverable conventions
type ComponentPropsForApp = DSControlledDateTimePickerT.DateTimePickerProps;
type ComponentPropsInternals = DSControlledDateTimePickerT.InternalDateTimePickerProps;
type ComponentPropsDefaultProps = DSControlledDateTimePickerT.DefaultDateTimeProps;
type ComponentPropsOptionalProps = DSControlledDateTimePickerT.OptionalDateTimeProps;
type ComponentPropsRequiredProps = DSControlledDateTimePickerT.RequiredDateTimeProps;

const testRequiredProps: ComponentPropsRequiredProps = {
  dateTime: '10/07/2023',
  onDateTimeChange: (dateTime: string) => {},
  type: 'full-date-time',
};
const testOptionalProps: ComponentPropsOptionalProps = {};

// difference Props and InternalProps is that InternalProps has all the default props filled in
//  Props allows for partial defaults
const testPartialDefaults: Partial<ComponentPropsDefaultProps> = {};
const testProps: ComponentPropsForApp = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testPartialDefaults,
};
const testPropsAsSyntax = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testPartialDefaults,
} as ComponentPropsForApp;
// InternalProps requires all defaults to be filled in
const testCompleteDefaults: Required<ComponentPropsDefaultProps> = {
  autoFocus: false,
  disabled: false,
  readOnly: false,
  getIsDayInRange: (day: string) => true,
  getIsDisabledDay: (day: string) => false,
  getIsDisabledTime: (time: string) => false,
  getIsEndRangeDay: (day: string) => false,
  getIsStartRangeDay: (day: string) => false,
  getIsOutOfRangeDay: (day: string) => false,
  hasError: false,
  isClearable: false,
  minutesInterval: 1,
  onDateChange: (date: string) => {},
  onTimeChange: (time: string) => {},
  onDayChange: (day: string) => {},
  onMonthChange: (month: string) => {},
  onYearChange: (year: string) => {},
  onHourChange: (hour: string) => {},
  onMinuteChange: (minute: string) => {},
  onMeridiemChange: (meridiem: string) => {},
  onPickerClose: () => {},
  onPickerOpen: () => {},
  preventCloseOnSelection: false,
};
const testInternalProps: ComponentPropsInternals = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testCompleteDefaults,
};
const testInternalPropsAsSyntax = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testCompleteDefaults,
} as ComponentPropsInternals;

// using the explicit type definition, if there is an error, it will be marked on the key that is wrong
const testExplicitDefinition: ComponentPropsForApp = {
  dateTime: '10/07/2023',
  onDateTimeChange: (dateTime: string) => {},
  type: 'full-date-time',
};

// using the "as" syntax, if there is an error, it will be marking the whole object as wrong because it is not compatible with the type
const testInferedTypeCompatibility = {
  dateTime: '10/07/2023',
  onDateTimeChange: (dateTime: string) => {},
  type: 'full-date-time',
} as ComponentPropsForApp;

const testDefinitionAsConst = {
  dateTime: '10/07/2023',
  onDateTimeChange: (dateTime: string) => {},
  type: 'full-date-time',
} as const;

const ExampleUsageComponent = () => (
  <>
    {/* works with explicitly casted props, all syntaxes */}
    <DSControlledDateTimePicker {...testExplicitDefinition} />
    <DSControlledDateTimePicker {...testInferedTypeCompatibility} />
    <DSControlledDateTimePicker {...testDefinitionAsConst} />
    {/* works with inline values */}
    <DSControlledDateTimePicker
      dateTime="10/07/2023"
      onDateTimeChange={(dateTime: string) => {}}
      type="full-date-time"
    />
  </>
);
